// Phase 25 iter-5b — hook compartido para o estado KYC.
// Mantén un polling lixeiro mentres o estado é PENDING/SUBMITTED para
// que a UI actualice cando o webhook de DIDIT chegue no backend.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { KycStatusResponse } from '@leyrochain/shared';

import { fetchKycStatus } from '../api/kyc';
import { useAuthContext } from '../context/AuthContext';

interface UseKycStatusResult {
  status: KycStatusResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const POLL_INTERVAL_MS = 30_000;

export function useKycStatus(): UseKycStatusResult {
  const { accessToken } = useAuthContext();
  const [status, setStatus] = useState<KycStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await fetchKycStatus();
      if (cancelledRef.current) return;
      setStatus(data);
      setError(null);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : 'kyc status failed');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    cancelledRef.current = false;
    void refresh();
    return () => {
      cancelledRef.current = true;
    };
  }, [refresh]);

  // Poll só mentres está PENDING ou SUBMITTED — DIDIT pode tardar uns
  // segundos en notificarnos vía webhook tras o usuario completar a
  // verificación. Sen polling, o user vería estado obsoleto ata refresh
  // manual.
  useEffect(() => {
    if (!status) return;
    if (status.status !== 'PENDING' && status.status !== 'SUBMITTED') return;
    const interval = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [status, refresh]);

  return { status, loading, error, refresh };
}
